import * as React from 'react';
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { CurrencyRateConfigurationChangeLogActionType, ICurrencyRateConfigurationChangeLogList } from '../../../types';
import { formatDateTimeToShowUserFromSaleOrderApiValue } from '../../../../common/helpers';

type CurrencyRateConfigLogsModalProps = {
	open: boolean;
	coinCode: string;
	logs: ICurrencyRateConfigurationChangeLogList[];
	onClose: () => void;
};

const actionLabel: Record<CurrencyRateConfigurationChangeLogActionType, string> = {
	[CurrencyRateConfigurationChangeLogActionType.ChangeSpread]: 'Cambio de spread',
	[CurrencyRateConfigurationChangeLogActionType.ChangeLockMode]: 'Cambio de modo de bloqueo',
	[CurrencyRateConfigurationChangeLogActionType.ChangeLockUntil]: 'Cambio de fecha de bloqueo',
};

const truncateWords = (text: string, n = 10): string => {
	if (!text) return '—';
	const words = text.trim().split(/\s+/);
	return words.length <= n ? text : `${words.slice(0, n).join(' ')}…`;
};

const normalize = (s: string) =>
	(s ?? '')
		.toString()
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');

export const CurrencyRateConfigurationChangeLogsModal = ({
	open,
	coinCode,
	logs,
	onClose,
}: CurrencyRateConfigLogsModalProps): JSX.Element => {
	const [query, setQuery] = React.useState('');

	const handleDialogClose = (_event: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
		if (reason === 'backdropClick') return; // <-- no cerrar por click afuera
		onClose();
	};

	const filtered = React.useMemo(() => {
		if (!query) return logs;
		const q = normalize(query);
		return logs.filter((l) => {
			const actionTypeFormatted = actionLabel[l.actionType] || l.actionType;
			const executedAtFormatted = formatDateTimeToShowUserFromSaleOrderApiValue(l.executedAt);
			const cols = [l.coinCode, l.executedByName, l.changeDescription, executedAtFormatted, actionTypeFormatted].map(
				(v) => normalize(v as string),
			);
			return cols.some((c) => c.includes(q));
		});
	}, [logs, query]);

	return (
		<Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ pr: 6 }}>
				Logs de cambios de la configuración de la moneda {coinCode}
				<IconButton onClick={onClose} aria-label="Cerrar" sx={{ position: 'absolute', right: 8, top: 8 }} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			{/* Toolbar de búsqueda */}
			<Box px={2} pt={1} pb={1.5}>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					spacing={1.5}
					alignItems={{ xs: 'stretch', sm: 'center' }}
					justifyContent="space-between"
				>
					<TextField
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder="Buscar en Acción realizada, Fecha de ejecución, Ejecutado por o Descripción…"
						size="small"
						fullWidth
						sx={{ flex: 1, minWidth: 260, maxWidth: 670 }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
						}}
						aria-label="Buscar logs"
					/>
					<Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
						Mostrando {filtered.length} de {logs.length}
					</Typography>
				</Stack>
			</Box>
			<Divider />

			<DialogContent dividers sx={{ p: 0 }}>
				<TableContainer sx={{ maxHeight: 480 }}>
					<Table stickyHeader size="small" aria-label="currency-config-logs">
						<TableHead>
							<TableRow>
								<TableCell>Acción realizada</TableCell>
								<TableCell>Fecha de ejecución</TableCell>
								<TableCell>Ejecutado por</TableCell>
								<TableCell>Descripción</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filtered.map((l) => (
								<TableRow key={l.id} hover>
									<TableCell>{actionLabel[l.actionType] ?? l.actionType}</TableCell>
									<TableCell>{formatDateTimeToShowUserFromSaleOrderApiValue(l.executedAt)}</TableCell>
									<TableCell>{l.executedByName}</TableCell>
									<TableCell sx={{ cursor: 'pointer' }}>
										<Tooltip title={l.changeDescription} arrow placement="top">
											<span>{truncateWords(l.changeDescription, 10)}</span>
										</Tooltip>
									</TableCell>
								</TableRow>
							))}
							{filtered.length === 0 && (
								<TableRow>
									<TableCell colSpan={4}>
										<Box p={2} textAlign="center" color="text.secondary">
											Sin resultados.
										</Box>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</DialogContent>
		</Dialog>
	);
};

export default CurrencyRateConfigurationChangeLogsModal;
